import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Skeleton } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import UtteranceRow from '../coding/UtteranceRow';
import { getWholeLesson } from '../../services/adjudicationService';
import { Panel } from './parts';

// The target in its ±2 window, or the whole lesson scrolled to it (wf-adj-item-blind 1–2). Speakers
// and text only: no labels of any layer for the other utterances. Opening the whole lesson is
// recorded by the endpoint; nothing here tells the adjudicator it is unusual.
const ContextCard = ({ runId, item, coding, onWholeLessonChange }) => {
    const [whole, setWhole] = useState(null);
    const [state, setState] = useState('window');
    const targetRef = useRef(null);

    useEffect(() => {
        if (state === 'whole' && targetRef.current) {
            targetRef.current.scrollIntoView?.({ block: 'center' });
        }
    }, [state]);

    const open = async () => {
        setState('loading');
        onWholeLessonChange?.(true);
        try {
            setWhole(whole || await getWholeLesson(runId, item.id));
            setState('whole');
        } catch (err) {
            setState('error');
        }
    };

    const close = () => {
        setState('window');
        onWholeLessonChange?.(false);
    };

    const showWhole = state !== 'window';
    const title = showWhole
        ? `Whole lesson${whole ? ` · ${whole.length} utterances` : ''}${coding ? ' · code the highlighted one' : ''}`
        : `Context${coding ? ' · code the highlighted utterance' : ''}`;

    return (
        <Panel title={title}>
            {state === 'loading' && [0, 1, 2, 3, 4].map((i) => <Skeleton key={i} height={36} />)}
            {state === 'error' && (
                <Alert severity="error" action={<Button color="inherit" size="small" onClick={open}>Try again</Button>}>
                    Couldn’t load the lesson.
                </Alert>
            )}
            {state === 'whole' && (
                <Box sx={{ maxHeight: { xs: '55vh', md: 520 }, overflowY: 'auto', mx: -1, px: 1, display: 'grid', gap: 0.25 }}>
                    {whole.map((u) => (
                        <UtteranceRow
                            key={u.number}
                            ref={u.target ? targetRef : undefined}
                            number={u.number}
                            speaker={u.speaker}
                            text={u.text}
                            variant={u.target ? 'target' : 'context'}
                        />
                    ))}
                </Box>
            )}
            {state === 'window' && (
                <Box sx={{ display: 'grid', gap: 0.5 }}>
                    {item.window.map((row, i) => (row
                        ? <UtteranceRow key={row.number} number={row.number} speaker={row.speaker} text={row.text} variant={row.offset === 0 ? 'target' : 'context'} />
                        // Start or end of the lesson: the slot stays empty
                        : <Box key={`empty-${i}`} aria-hidden sx={{ height: 40 }} />))}
                </Box>
            )}
            <Button
                size="small"
                variant="text"
                onClick={showWhole ? close : open}
                endIcon={showWhole ? <ExpandLess /> : <ExpandMore />}
                sx={{ mt: 1.5, ml: -1 }}
            >
                {showWhole ? 'Back to ±2' : 'Show the whole lesson'}
            </Button>
        </Panel>
    );
};

export const ContextSkeleton = () => (
    <Panel title="Context">
        {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} height={40} />)}
    </Panel>
);

export default ContextCard;
